import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { environment } from '../../../environments/environment';

/**
 * CloverGoProvisioningService — seeds the *non-secret* half of the Clover Go
 * device configuration into Capacitor Preferences (the clover_go.* namespace the
 * native CloverGoPlugin reads).
 *
 * Credential model (reconciled with the real Clover dashboard + Developer Relations):
 *
 * The Go SDK needs TWO distinct credential pairs:
 *   1. The OAuth App ID + App Secret (client_id / clientSecret) — used for the
 *      OAuth flow / merchant token, and already in the bundle for the app's
 *      existing Clover OAuth.
 *   2. A DEDICATED Go SDK API key + API secret, issued SEPARATELY by Clover
 *      Developer Relations (NOT visible in the dashboard alongside the App
 *      ID/Secret). The Go SDK Builder's apiKey/apiSecret slots require THIS pair —
 *      the earlier assumption that App ID/Secret could satisfy them was wrong and
 *      is exactly why the dev-kit reader paired but never completed RKI ("SDK not
 *      ready"). Sandbox pair is bundled for dev builds; the production pair ships
 *      via MDM managed config, never in the bundle.
 *
 *   Seeded here from app state already on the device:
 *     - clover_go.app_id      = environment.client_id   (bare Clover App ID)
 *     - clover_go.app_secret  = environment.clientSecret (Clover App Secret)
 *     - clover_go.env         = 'production' | 'sandbox' (from build env)
 *     - clover_go.merchant_id = currentUser.orgSettings.merchantId (post-login)
 *     - clover_go.api_key     = environment.cloverGoSandboxApiKey    (DEV only)
 *     - clover_go.api_secret  = environment.cloverGoSandboxApiSecret (DEV only)
 *
 *   Per-merchant secret, NEVER seeded here and NEVER compiled into the bundle:
 *     - clover_go.access_token (merchant Clover access token — obtained through the
 *                               normal Clover OAuth flow, or pushed per-device by
 *                               Driven MDM as managed config, the same mechanism
 *                               that already delivers `device_activation_code`).
 *   Production Go SDK api_key/api_secret arrive the same MDM way (the sandbox
 *   fields are absent from environment.prod.ts, so production seeds nothing here).
 *
 * The access token is the one truly per-merchant credential and stays out of the
 * APK: baking a live merchant token into every build artifact is exactly what this
 * split exists to prevent.
 *
 * Seeding is idempotent and MDM-first: a value is written ONLY when the key is
 * absent/blank, so an MDM push (or a manual override) always wins and a stale
 * seed can never clobber a real credential.
 */
@Injectable({ providedIn: 'root' })
export class CloverGoProvisioningService {
  private static readonly APP_ID_KEY = 'clover_go.app_id';
  private static readonly APP_SECRET_KEY = 'clover_go.app_secret';
  private static readonly ENV_KEY = 'clover_go.env';
  private static readonly MERCHANT_ID_KEY = 'clover_go.merchant_id';
  private static readonly ACCESS_TOKEN_KEY = 'clover_go.access_token';
  private static readonly API_KEY_KEY = 'clover_go.api_key';
  private static readonly API_SECRET_KEY = 'clover_go.api_secret';

  constructor(private http: HttpClient) {}

  /**
   * Seed the build-time config (Clover App ID + App Secret + environment). Safe to
   * call on every native boot; a no-op off-device.
   *
   * App ID/Secret/env are BUILD-TIME FACTS: they are file-replaced per build
   * (environment.ts = SANDBOX app creds -> sandbox.dev.clover.com, environment.prod.ts
   * = PRODUCTION app creds -> api.clover.com), so the running bundle is the single
   * source of truth for them and they are seeded AUTHORITATIVELY — the seed always
   * corrects the stored value to match the current build. This is deliberate: a
   * physical Clover Go reader is Remote-Key-Injected (RKI) against one Clover world
   * and only reaches Ready when the SDK authenticates the MATCHING app credentials
   * in that SAME world. If a device previously installed a build whose env/app-id
   * disagreed (e.g. sandbox app creds pinned to a 'production' env seed), the reader
   * pairs over Bluetooth but never completes RKI ("connecting -> Reader not
   * connected"). Because setIfBlank never overwrites, such a stale seed would
   * otherwise survive every reinstall; overwriting here is what unsticks it.
   */
  async seedFromEnvironment(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    // Bare Clover App ID (sandbox 7HPEPVBTZGDCP / prod 17Y5YKPG131ZM), NOT the
    // merchant-prefixed connector id.
    const appId = (environment as any).client_id || (environment as any).clientId;
    if (appId) {
      await this.setAuthoritative(CloverGoProvisioningService.APP_ID_KEY, String(appId));
    }
    const appSecret = (environment as any).clientSecret || (environment as any).client_secret;
    if (appSecret) {
      await this.setAuthoritative(CloverGoProvisioningService.APP_SECRET_KEY, String(appSecret));
    }
    // The Go SDK environment MUST track the Clover OAuth environment the rest of
    // the app already uses, or app creds and env end up in different Clover worlds:
    //   production build -> Clover PROD, dev build -> Clover SANDBOX (dev-kit reader).
    const env = environment.production ? 'production' : 'sandbox';
    await this.setAuthoritative(CloverGoProvisioningService.ENV_KEY, env);

    // Dedicated Go SDK API key/secret (Clover Developer Relations pair) — the
    // credentials the SDK actually authenticates with so the dev-kit reader can
    // complete RKI and reach Ready. In DEV these come from the bundled SANDBOX
    // pair; they are ABSENT from environment.prod.ts, so production seeds nothing
    // here and instead receives the PRODUCTION pair via MDM managed config (the
    // guard below is false in prod). Seeded AUTHORITATIVELY for the same reason as
    // the app creds above: a device that ran an earlier build with the wrong (or
    // no) Go SDK key self-corrects on the next boot instead of a stale value
    // surviving reinstall.
    const goApiKey = (environment as any).cloverGoSandboxApiKey;
    const goApiSecret = (environment as any).cloverGoSandboxApiSecret;
    if (goApiKey && goApiSecret) {
      await this.setAuthoritative(CloverGoProvisioningService.API_KEY_KEY, String(goApiKey));
      await this.setAuthoritative(CloverGoProvisioningService.API_SECRET_KEY, String(goApiSecret));
    }
  }

  /**
   * Seed the merchant id once it is known (after login / session restore). The
   * Go SDK pairs the reader to this merchant so a sale reconciles like any other
   * Clover payment. Best-effort and idempotent.
   */
  async seedMerchant(merchantId: string | null | undefined): Promise<void> {
    if (!Capacitor.isNativePlatform() || !merchantId) {
      return;
    }
    await this.setIfBlank(CloverGoProvisioningService.MERCHANT_ID_KEY, String(merchantId));
  }

  /**
   * Seed the per-merchant Clover access token (Route A — server-seed at boot).
   * Calls the existing /api/clientCredentials/getCloverToken endpoint, which
   * decrypts and returns the merchant's plaintext Clover token. Stored only in
   * Capacitor Preferences — never logged, never returned to the caller.
   *
   * Idempotent and MDM-first: skipped when the key is already populated so that
   * an MDM push or a prior seed always wins.
   */
  async seedAccessToken(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      // Dev/sandbox builds: the Clover Go SDK requires an EMPLOYEE-BOUND OAuth
      // token (authorization-code + PKCE), which only the interactive
      // OAuthFlowType.Full login can mint. The sandbox "dev-kit" value we used to
      // seed here is a Clover DASHBOARD API token — the SDK rejects it with
      // "Unable to fetch Merchant and Employee info" because it carries no
      // employee binding. So in dev we DO NOT seed a token: we actively REMOVE any
      // stale one (e.g. left behind by an earlier build) so the plugin builds the
      // SDK with OAuthFlowType.Full and drives the interactive login, which returns
      // through the grubgrab.net/cloverAuth App Link. Merchant id is still seeded.
      if (!environment.production) {
        const devMerchant = (environment as any).cloverGoSandboxMerchantId;
        if (devMerchant) {
          await this.setIfBlank(CloverGoProvisioningService.MERCHANT_ID_KEY, String(devMerchant));
        }
        await Preferences.remove({ key: CloverGoProvisioningService.ACCESS_TOKEN_KEY });
        return;
      }
      // Production: mint a real per-merchant, employee-bound token server-side and
      // inject it via NoOAuth — no browser round-trip, no App Link needed.
      const existing = await Preferences.get({ key: CloverGoProvisioningService.ACCESS_TOKEN_KEY });
      if (existing?.value && existing.value.trim().length) {
        return;
      }
      const res: any = await this.http
        .post(`${environment.apiUrl}/clientCredentials/getCloverToken`, {})
        .toPromise();
      if (res?.cloverToken) {
        await Preferences.set({ key: CloverGoProvisioningService.ACCESS_TOKEN_KEY, value: res.cloverToken });
      }
    } catch {
      // Best-effort — the plugin stays in "not configured" state until the
      // token is present. The next boot will retry automatically.
    }
  }

  /** Pull the merchant id off whatever shape the current user object carries. */
  merchantIdFromUser(user: any): string | null {
    if (!user) {
      return null;
    }
    return user.orgSettings?.merchantId || user.merchantId || null;
  }

  /**
   * Authoritatively set a BUILD-TIME fact (app_id / app_secret / env). Unlike
   * setIfBlank this corrects a stale value so a device that installed a
   * wrong-environment build is fixed on the next boot. Skips the write when the
   * stored value already matches, to avoid needless Preferences churn. Never used
   * for per-merchant secrets (merchant_id / access_token stay MDM-first).
   */
  private async setAuthoritative(key: string, value: string): Promise<void> {
    try {
      const existing = await Preferences.get({ key });
      if (existing?.value === value) {
        return; // already correct — no write needed.
      }
      await Preferences.set({ key, value });
    } catch {
      // Preferences unavailable (non-native / storage error) — best-effort.
    }
  }

  private async setIfBlank(key: string, value: string): Promise<void> {
    try {
      const existing = await Preferences.get({ key });
      if (existing?.value && existing.value.trim().length) {
        return; // MDM / prior provisioning already set it — never overwrite.
      }
      await Preferences.set({ key, value });
    } catch {
      // Preferences unavailable (non-native / storage error) — provisioning is
      // best-effort; the plugin simply reports "not configured" until the keys
      // exist, and the register keeps its existing flows.
    }
  }
}
